import type { GamePlayer, PlayerDataUpdate } from "./";

// Common message properties
type CommonProps = {
  conversationId?: string;
};

// Only export message types that games need to know about
export type BeginPlatformLoginMessage = CommonProps & {
  type: "BeginPlatformLogin";
  entryPayload?: string;
};

export type BeginPlatformPurchaseMessage = CommonProps & {
  type: "BeginPlatformPurchase";
  productSku: string;
};

export type BeginPlatformPurchaseResponseMessage = CommonProps &
  ({
    type: "BeginPlatformPurchaseResponse";
  } & (
    | {
        result: "success";
        purchaseToken: string;
      }
    | {
        result: "cancel";
      }
    | {
        result: "error";
        error: "internal_error" | "purchase_failed" | "invalid_product";
      }
  ));

export type AcquireLoginLeaseMessage = CommonProps & {
  type: "AcquireLoginLease";
  message: string;
  keywords: string[] | null;
  entryPayload?: string;
};

export type RedirectToGameMessage = CommonProps & {
  type: "RedirectToGame";
  entryPayload?: string;
} & ({ redirectToFlagship: true } | { gameSlug: string });

export type OpenLegalPageMessage = CommonProps & {
  type: "OpenLegalPage";
  page: "privacy" | "terms" | "copyright";
};

export type InitializedMessage = CommonProps & {
  type: "Initialized";
};

export type SetPlayerMessage = CommonProps & {
  type: "SetPlayer";
  player: GamePlayer;
};

export type ScheduleNotificationV2Message = CommonProps & {
  type: "ScheduleNotificationV2";
  scheduledAt: string; // ISO string
  entryPayload?: Record<string, unknown>;
  identifier?: string;
  image: string;
  ctaText: string;
  body: string;
  fallbackBody: string;
  priority: "low" | "medium" | "high" | "critical";
};

export type ScheduleNotificationV1Message = CommonProps & {
  type: "ScheduleNotification";
  message: string;
  scheduledAt: string; // ISO string
  attemptPushNotification: boolean;
  entryPayload?: Record<string, unknown>;
  identifier?: string;
};

export type UnscheduleNotificationMessage = CommonProps & {
  type: "UnscheduleNotification";
  identifier: string;
};

export type ScheduleNotificationMessage =
  | ScheduleNotificationV1Message
  | ScheduleNotificationV2Message;

export type CaptureEventMessage = CommonProps & {
  type: "CaptureEvent";
  event: string;
  properties: Record<string, unknown>;
};

export const validOnboardingEvents = [
  "sms_modal_show",
  "exit_popup_show",
  "continue_exit_click",
  "abandon_exit_click",
] as const;
export type OnboardingAnalyticsEvent = (typeof validOnboardingEvents)[number];

export type OnboardingCaptureEventMessage = CommonProps & {
  type: "OnboardingCaptureEvent";
  event: "first_interaction" | "sms_button_tap" | OnboardingAnalyticsEvent;
};

// Export only the message types games need to handle
export type UpdatePlayerDataMessage = CommonProps & {
  type: "UpdatePlayerData";
  id: number;
  update: PlayerDataUpdate;
};

export type AckUpdatePlayerDataMessage = CommonProps & {
  type: "AckUpdatePlayerData";
  id: number;
};

export type LoginLeaseAcquiredMessage = CommonProps &
  (
    | {
        type: "LoginLeaseAcquired";
        success: true;
        link: string;
      }
    | {
        type: "LoginLeaseAcquired";
        success: false;
        error: "internal_error";
        link: "";
      }
  );

export type CreateLinkMessage = CommonProps & {
  type: "CreateLink";
  gameSlug: string;
  entryPayload?: string;
};

export type CreateLinkResponseMessage = CommonProps & {
  type: "CreateLinkResponse";
  success: boolean;
  link: string;
};

export type GetGameProductsMessage = CommonProps & {
  type: "GetGameProducts";
};

export type GetGameProductsResponseMessage = CommonProps & {
  type: "GetGameProductsResponse";
  success: boolean;
  products: Array<{
    sku: string;
    name: string;
    description: string | null;
    price: number;
  }>;
};

export type SdkMessage =
  | SetPlayerMessage
  | AckUpdatePlayerDataMessage
  | LoginLeaseAcquiredMessage
  | CreateLinkResponseMessage
  | GetGameProductsResponseMessage
  | BeginPlatformPurchaseResponseMessage;

export type HostMessage =
  | BeginPlatformLoginMessage
  | BeginPlatformPurchaseMessage
  | InitializedMessage
  | ScheduleNotificationMessage
  | UnscheduleNotificationMessage
  | CaptureEventMessage
  | UpdatePlayerDataMessage
  | AcquireLoginLeaseMessage
  | CreateLinkMessage
  | RedirectToGameMessage
  | OpenLegalPageMessage
  | OnboardingCaptureEventMessage
  | GetGameProductsMessage;

export type Message = SdkMessage | HostMessage;

export type MessageType = Message["type"];
export type SdkMessageType = SdkMessage["type"];
export type HostMessageType = HostMessage["type"];

/**
 * The matching Message type with a given type value (T).
 * Useful for generically typing message when the type value is not yet known
 */
export type MessageByType<T extends MessageType> = Extract<
  Message,
  { type: T }
>;

export type SdkMessageByType<T extends SdkMessageType> = Extract<
  SdkMessage,
  { type: T }
>;

export type HostMessageByType<T extends HostMessageType> = Extract<
  HostMessage,
  { type: T }
>;

export function isTextClubMessage(
  data: unknown,
): data is SdkMessage | HostMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    "source_channel" in data &&
    (data as { source_channel: string }).source_channel === "textclub"
  );
}

export interface ChildBridge {
  sendMessage(message: HostMessage): void;
  onMessage(message: SdkMessage): void;
}

export interface HostBridge {
  sendMessage(message: SdkMessage): void;
  onMessage(message: HostMessage): void;
}
